// Browser-native `EventSource` wrapper for the BFF SSE re-fan.
//
// **Why this lives in the SPA (not in `@sb-ai-platform/bff`):**
//   - It is THE consumer side of the SSE protocol the BFF re-fans
//     from the control plane. The BFF emits the protocol; the SPA
//     consumes it. The wire shape is plain JSON SSE frames (one
//     per `data: ...` line).
//
// **Why we use `EventSource` directly — NOT `httpSubscriptionLink`:**
//   - Spec § Streaming (round-3 disambiguation): tRPC is reserved for
//     queries + mutations only. The SPA never sets up an
//     `httpSubscriptionLink`. Two transports, two purposes — keeping
//     them separate lets us evolve either one without churning the
//     other.
//   - `EventSource` already implements: automatic reconnect on close,
//     `Last-Event-ID` header on reconnect (when the server emits
//     `id: <opaque>` lines), retry-after honor, send-cookies-with-
//     credentials. We re-invent NONE of that.
//
// **Cookie carriage:** the BFF cookie-session plugin authenticates the
// SSE GET via the `sbp_session` cookie. Cross-origin EventSource
// requires `withCredentials: true` to send the cookie. Same-origin
// (which is the dev-proxy case) sends the cookie regardless. We pass
// `withCredentials: true` ALWAYS — safe in both cases.
//
// **Reconnect semantics:** the browser handles reconnect with
// `Last-Event-ID` IF the server emits `id: <opaque>` lines. The
// control plane DOES emit those (per spec § Streaming — event IDs are
// opaque, never compared). On reconnect, the BFF re-fan forwards
// `Last-Event-ID` to the control plane, which re-polls MA from the
// cursor.
//
// **Backpressure:** EventSource buffers internally; we expose a
// `subscribe(listener)` API. Listeners that block the dispatch loop
// will slow event delivery — kept simple by design; consumers are
// expected to push events into a React store and let React schedule
// the re-render.
//
// **API SHAPE:** the wrapper exposes `onMessage(listener)`,
// `onError(listener)`, `close()`. It DOES NOT couple to React — that
// lives in `streams/use-session-stream.ts` (fn-1.29) which will wire
// `useSyncExternalStore` over this primitive.

/**
 * The wire shape of an SSE frame the BFF emits. The BFF re-fans MA
 * events; the on-wire payload is the MA event JSON (passed through
 * verbatim). At v1 we don't validate the shape here — consumers
 * narrow as they go.
 *
 * **`raw`** carries the original `event.data` string. Consumers that
 * want full fidelity (rare) can use it; the typical consumer reads
 * `data` (the parsed JSON).
 */
export interface SessionStreamFrame {
  /** Parsed JSON payload from `event.data`. */
  readonly data: unknown;
  /** Original `event.data` string. */
  readonly raw: string;
  /** Opaque event id from the server (`id: ...`). May be empty when absent. */
  readonly lastEventId: string;
}

/** Listener types — narrow by function signature. */
export type FrameListener = (frame: SessionStreamFrame) => void;
export type ErrorListener = (error: Event) => void;
export type OpenListener = () => void;

/**
 * Options for `connectSessionStream`. Tests pass `eventSourceFactory`
 * to inject a fake. Production callers leave it undefined and get
 * `globalThis.EventSource`.
 */
export interface ConnectSessionStreamOptions {
  /**
   * BFF SSE URL. Typically `/sse/sessions/${id}/stream` — same-origin
   * via the Vite dev proxy or the production BFF.
   */
  readonly url: string;
  /**
   * Optional EventSource factory. Tests inject a fake; production uses
   * `globalThis.EventSource`.
   *
   * **Why an injection point rather than mocking `globalThis`:**
   * mocking `globalThis.EventSource` would race with parallel tests
   * sharing the same `happy-dom` instance. An explicit injection
   * point keeps each test's fake isolated to the test body.
   */
  readonly eventSourceFactory?: (url: string, init?: EventSourceInit) => EventSource;
  /**
   * Whether to send cookies cross-origin. Defaults to `true` — the
   * BFF lives at a different origin in production, but the cookie
   * plugin needs to receive `sbp_session`. Same-origin requests
   * (dev) ignore this flag.
   */
  readonly withCredentials?: boolean;
}

/**
 * Connection handle. Use to attach listeners + close the stream.
 *
 * **Why `close` returns void rather than `Promise<void>`:**
 *   - `EventSource.close()` is synchronous in the browser spec — the
 *     readyState transitions to `CLOSED` immediately, even though the
 *     underlying TCP socket may take a moment to FIN.
 *   - Wrapping in a Promise would suggest the caller should await,
 *     which is misleading: pending listeners do not fire after
 *     `close()`.
 *
 * **Listeners stay attached after `close()`** until the caller
 * explicitly drops the handle. This matches the lifecycle the
 * fn-1.29 `useSyncExternalStore` hook wants — the store's
 * `subscribe` returns a teardown that calls `handle.close()`; the
 * listener reference is released when the React component unmounts.
 */
export interface SessionStreamHandle {
  /** Current EventSource `readyState`. 0=CONNECTING, 1=OPEN, 2=CLOSED. */
  readonly readyState: () => number;
  /**
   * Register a frame listener. Each registered listener fires on every
   * `data` frame. Returns an unsubscribe function.
   */
  onMessage(listener: FrameListener): () => void;
  /**
   * Register an error listener. Fires on `EventSource` `error` events
   * (typically connection drops; the browser retries automatically).
   * Returns an unsubscribe function.
   */
  onError(listener: ErrorListener): () => void;
  /**
   * Register an open listener. Fires on initial connect + every
   * reconnect (`EventSource.onopen` semantics). Returns an
   * unsubscribe function.
   */
  onOpen(listener: OpenListener): () => void;
  /** Close the underlying EventSource. Idempotent. */
  close(): void;
}

/**
 * Open an SSE connection to the BFF re-fan endpoint.
 *
 * **Lifecycle:**
 *   1. Caller invokes with `url`. Wrapper constructs `new
 *      EventSource(url, { withCredentials: true })`.
 *   2. Browser dispatches an HTTP GET with the `sbp_session` cookie.
 *      On 401 the browser surfaces an `error` event; the SPA's auth
 *      layer (`auth/use-auth.ts` in fn-1.29) maps that to a re-login.
 *   3. On `message` events, the wrapper parses `event.data` as JSON
 *      and fans out to all `onMessage` listeners.
 *   4. On `error` events, fans out to all `onError` listeners. The
 *      EventSource auto-reconnects per its own retry schedule
 *      (initially ~3s, server-tunable via `retry: <ms>` SSE field).
 *   5. On `close()`, the wrapper calls `EventSource.close()`. No
 *      more frames will arrive.
 *
 * **Parse failures** are surfaced as `error` events (we synthesize an
 * `Event('error')` since the parse failure is a wrapper-level issue,
 * not an EventSource one). The original frame is logged; consumers
 * downstream see a normal error and recover.
 */
export function connectSessionStream(opts: ConnectSessionStreamOptions): SessionStreamHandle {
  if (typeof opts.url !== 'string' || opts.url.length === 0) {
    throw new Error('connectSessionStream: url is required');
  }

  const factory =
    opts.eventSourceFactory ??
    ((url: string, init?: EventSourceInit) => new EventSource(url, init));
  const withCredentials = opts.withCredentials ?? true;

  const es = factory(opts.url, { withCredentials });

  const messageListeners = new Set<FrameListener>();
  const errorListeners = new Set<ErrorListener>();
  const openListeners = new Set<OpenListener>();
  let closed = false;

  const handleMessage = (ev: MessageEvent): void => {
    if (closed) return;
    // `event.data` is `string | null` per the DOM spec; defend
    // against the null branch even though the browser never emits
    // it on a `message` event (only on closed streams in some
    // implementations).
    const raw = typeof ev.data === 'string' ? ev.data : '';
    let parsed: unknown;
    try {
      parsed = raw.length === 0 ? null : JSON.parse(raw);
    } catch (err) {
      // Parse failure — fan out as an error so the SPA's error UI
      // surfaces it. Log for the developer; the user-facing
      // behavior is just a retry on the next frame.
      // eslint-disable-next-line no-console
      console.warn('[session-stream] failed to parse frame', { raw, err });
      const synthetic = new Event('error');
      for (const l of errorListeners) {
        try {
          l(synthetic);
        } catch {
          // ignore listener errors so one bad listener can't
          // poison the dispatch loop.
        }
      }
      return;
    }
    const frame: SessionStreamFrame = {
      data: parsed,
      raw,
      lastEventId: typeof ev.lastEventId === 'string' ? ev.lastEventId : '',
    };
    for (const l of messageListeners) {
      try {
        l(frame);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[session-stream] message listener threw', err);
      }
    }
  };

  const handleError = (ev: Event): void => {
    if (closed) return;
    for (const l of errorListeners) {
      try {
        l(ev);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[session-stream] error listener threw', err);
      }
    }
  };

  const handleOpen = (): void => {
    if (closed) return;
    for (const l of openListeners) {
      try {
        l();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[session-stream] open listener threw', err);
      }
    }
  };

  es.addEventListener('message', handleMessage);
  es.addEventListener('error', handleError);
  es.addEventListener('open', handleOpen);

  return {
    readyState: () => es.readyState,
    onMessage(listener) {
      messageListeners.add(listener);
      return () => {
        messageListeners.delete(listener);
      };
    },
    onError(listener) {
      errorListeners.add(listener);
      return () => {
        errorListeners.delete(listener);
      };
    },
    onOpen(listener) {
      openListeners.add(listener);
      return () => {
        openListeners.delete(listener);
      };
    },
    close() {
      if (closed) return;
      closed = true;
      es.removeEventListener('message', handleMessage);
      es.removeEventListener('error', handleError);
      es.removeEventListener('open', handleOpen);
      es.close();
      messageListeners.clear();
      errorListeners.clear();
      openListeners.clear();
    },
  };
}

/**
 * Live-preview opt-in query parameter, sent unconditionally: every browser
 * consumer of this surface wants token previews, and a control plane without
 * the feature simply ignores it — no version negotiation. Non-browser consumers
 * (sbp, smoke harnesses) build their own URLs and keep paying zero delta cost.
 */
export const PREVIEW_OPT_IN_QUERY = 'preview=1';

/**
 * Convenience — build the BFF SSE URL for a session id.
 *
 * **Encoding:** `encodeURIComponent` on the session id keeps the URL
 * safe against `/` in opaque ids (none observed at v1, but defensive).
 * `basePath` defaults to `/sse/sessions`; tests can override.
 */
export function buildSessionStreamUrl(sessionId: string, basePath = '/sse/sessions'): string {
  if (typeof sessionId !== 'string' || sessionId.length === 0) {
    throw new Error('buildSessionStreamUrl: sessionId is required');
  }
  if (typeof basePath !== 'string' || basePath.length === 0) {
    throw new Error('buildSessionStreamUrl: basePath is required');
  }
  const trimmed = basePath.endsWith('/') ? basePath.slice(0, -1) : basePath;
  return `${trimmed}/${encodeURIComponent(sessionId)}/stream?${PREVIEW_OPT_IN_QUERY}`;
}
